import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api-client';
import { StatusBadge } from '@/components/shared/status-badge';
import type {
  CustomRecognizer,
  SwapLanguage,
  SwapProfile,
  SwapProfileInput,
} from '@/lib/types';

function parseList(s: string): string[] {
  return s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

export function SwapProfiles(): JSX.Element {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SwapProfile | null>(null);

  const profiles = useQuery({
    queryKey: ['/api/admin/swap-profiles'],
    queryFn: () => api.get<SwapProfile[]>('/api/admin/swap-profiles'),
  });

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/swap-profiles'] });
  };

  const create = useMutation({
    mutationFn: (v: SwapProfileInput) => api.post<SwapProfile>('/api/admin/swap-profiles', v),
    onSuccess: () => { invalidate(); setShowForm(false); },
  });
  const update = useMutation({
    mutationFn: ({ id, v }: { id: string; v: SwapProfileInput }) =>
      api.patch<SwapProfile>(`/api/admin/swap-profiles/${id}`, v),
    onSuccess: () => { invalidate(); setEditing(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/swap-profiles/${id}`),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Swap Profiles</h2>
        <button
          className="btn-primary"
          onClick={() => { setEditing(null); setShowForm((v) => !v); }}
        >
          <Plus className="h-4 w-4" /> {showForm ? 'Bezárás' : 'Új profil'}
        </button>
      </div>

      {(showForm || editing) && (
        <ProfileForm
          key={editing?.id ?? 'new'}
          initial={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={(v) => {
            if (editing) update.mutate({ id: editing.id, v });
            else create.mutate(v);
          }}
        />
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Név</th>
              <th className="px-4 py-2.5">Alapért.</th>
              <th className="px-4 py-2.5">Nyelvek</th>
              <th className="px-4 py-2.5">Autodetektálás</th>
              <th className="px-4 py-2.5">Alapnyelv</th>
              <th className="px-4 py-2.5">Passthrough</th>
              <th className="px-4 py-2.5">Letiltott felismerők</th>
              <th className="w-24 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {profiles.isLoading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Betöltés…</td></tr>
            )}
            {profiles.isError && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-rose-600">Hiba: {String(profiles.error)}</td></tr>
            )}
            {profiles.data?.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Nincs profil.</td></tr>
            )}
            {profiles.data?.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium">{p.name}</td>
                <td className="px-4 py-2.5">
                  {p.isDefault ? <StatusBadge label="Alapért." tone="green" /> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {p.languages.map((l) => (
                      <span
                        key={l.code}
                        title={l.spacyModel}
                        className={
                          l.enabled
                            ? 'rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600'
                            : 'rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 line-through'
                        }
                      >
                        {l.code}
                      </span>
                    ))}
                    {p.languages.length === 0 && <span className="text-slate-300">—</span>}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  {p.autoDetectLanguage ? <span className="text-emerald-600">✓</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{p.defaultLanguage}</td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">
                  {p.passthroughEntityTypes.join(', ') || '—'}
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">
                  {p.disabledRecognizers.join(', ') || '—'}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <button
                      title="Szerkeszt"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      onClick={() => { setShowForm(false); setEditing(p); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Töröl"
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => { if (confirm(`Törlés: "${p.name}"?`)) remove.mutate(p.id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProfileForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: SwapProfile | null;
  onSubmit: (v: SwapProfileInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const isEdit = Boolean(initial);
  const [name, setName] = useState(initial?.name ?? '');
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [autoDetect, setAutoDetect] = useState(initial?.autoDetectLanguage ?? true);
  const [defaultLanguage, setDefaultLanguage] = useState(initial?.defaultLanguage ?? 'hu');
  const [passthrough, setPassthrough] = useState((initial?.passthroughEntityTypes ?? []).join(', '));
  const [disabled, setDisabled] = useState((initial?.disabledRecognizers ?? []).join(', '));
  const [languages, setLanguages] = useState<SwapLanguage[]>(
    initial?.languages ?? [{ code: 'hu', spacyModel: 'hu_core_news_lg', enabled: true }],
  );
  const [recognizerIds, setRecognizerIds] = useState<number[]>(initial?.recognizerIds ?? []);

  const recognizers = useQuery({
    queryKey: ['/api/admin/custom-recognizers'],
    queryFn: () => api.get<CustomRecognizer[]>('/api/admin/custom-recognizers'),
  });

  const toggleRecognizer = (id: number): void => {
    setRecognizerIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  const updateLang = (i: number, patch: Partial<SwapLanguage>): void => {
    setLanguages((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };
  const addLang = (): void => {
    setLanguages((ls) => [...ls, { code: '', spacyModel: '', enabled: true }]);
  };
  const removeLang = (i: number): void => {
    setLanguages((ls) => ls.filter((_, idx) => idx !== i));
  };

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    onSubmit({
      name,
      isDefault,
      languages: languages.filter((l) => l.code.trim()),
      autoDetectLanguage: autoDetect,
      defaultLanguage,
      passthroughEntityTypes: parseList(passthrough),
      disabledRecognizers: parseList(disabled),
      recognizerIds,
    });
  };

  return (
    <form className="card space-y-4 p-4" onSubmit={submit}>
      <div className="text-sm font-semibold text-slate-700">
        {isEdit ? `Swap profil szerkesztése: ${initial?.name}` : 'Új swap profil'}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="label">Név</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label">Alapnyelv</label>
          <input
            className="input"
            value={defaultLanguage}
            onChange={(e) => setDefaultLanguage(e.target.value)}
            placeholder="hu"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Alapértelmezett profil
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4" checked={autoDetect} onChange={(e) => setAutoDetect(e.target.checked)} />
          Nyelv automatikus felismerése
        </label>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="label">Nyelvek</label>
          <button type="button" className="btn-secondary" onClick={addLang}>
            <Plus className="h-4 w-4" /> Nyelv hozzáadása
          </button>
        </div>
        <div className="space-y-2">
          {languages.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="input w-24"
                placeholder="kód (hu)"
                value={l.code}
                onChange={(e) => updateLang(i, { code: e.target.value })}
              />
              <input
                className="input grow"
                placeholder="spaCy model (hu_core_news_lg)"
                value={l.spacyModel}
                onChange={(e) => updateLang(i, { spacyModel: e.target.value })}
              />
              <label className="flex items-center gap-1 whitespace-nowrap text-xs text-slate-600">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={l.enabled}
                  onChange={(e) => updateLang(i, { enabled: e.target.checked })}
                />
                aktív
              </label>
              <button
                type="button"
                className="rounded p-1 text-rose-500 hover:bg-rose-50"
                onClick={() => removeLang(i)}
                title="Eltávolít"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {languages.length === 0 && (
            <p className="text-xs text-slate-500">Nincs nyelv. Adj hozzá legalább egyet.</p>
          )}
        </div>
      </div>

      <div>
        <label className="label">Csatolt custom szűrők</label>
        <div className="flex flex-wrap gap-2">
          {recognizers.data?.length === 0 && (
            <p className="text-xs text-slate-500">
              Nincs custom szűrő. Hozz létre egyet a „Custom szűrők" oldalon.
            </p>
          )}
          {recognizers.data?.map((r) => {
            const on = recognizerIds.includes(r.id);
            return (
              <button
                type="button"
                key={r.id}
                onClick={() => toggleRecognizer(r.id)}
                className={
                  on
                    ? 'rounded-md border border-echo-gold bg-echo-gold/10 px-2.5 py-1 text-xs font-medium text-heading'
                    : 'rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted'
                }
                title={r.entityLabel}
              >
                {r.name} <code className="opacity-60">[{r.entityLabel}]</code>
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          A kijelölt szűrőket ez a profil (és a rá épülő deploymentek) használja.
        </p>
      </div>

      <div>
        <label className="label">Passthrough entitástípusok (vesszővel)</label>
        <input
          className="input font-mono text-xs"
          value={passthrough}
          onChange={(e) => setPassthrough(e.target.value)}
          placeholder="DATE_TIME, URL"
        />
        <p className="mt-1 text-xs text-slate-500">
          Ezek a típusok csere nélkül mennek át.
        </p>
      </div>

      <div>
        <label className="label">Letiltott entitások (vesszővel)</label>
        <input
          className="input font-mono text-xs"
          value={disabled}
          onChange={(e) => setDisabled(e.target.value)}
          placeholder="PERSON, ORG, ADDRESS"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Az itt felsorolt entitás-típusokat a profil nem cseréli. Érvényes:
          spaCy NER → PERSON, ORG, ADDRESS; Presidio beépített →
          EMAIL, PHONE, IBAN, CARD, CRYPTO, IP_ADDRESS, URL, DATE
          (+ a saját spaCy modell további címkéi).
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>Mégse</button>
        <button type="submit" className="btn-primary"><Plus className="h-4 w-4" /> Mentés</button>
      </div>
    </form>
  );
}
